// OnboardingContext.js - Manages onboarding state
// Uses onboarding_complete flag from profile to determine if user is new
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FASTAPI_BASE_URL } from '../config';
import { DEFAULT_BOARD } from '../constants/boards';

const OnboardingContext = createContext();

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

const defaultOnboardingData = {
  completed: false,
  subjects: [],
  learningGoal: 'regular',
  // Profile fields
  profile: {
    grade: '',
    age: '',
    passion: '',
    favorite_stem_subject: '',
    extracurricular_activity: '',
    preferred_learning_style: 'visual',
  },
  // Board/curriculum selection
  board: '',
};

export const OnboardingProvider = ({ children, userEmail, authToken, userData, refetchProfile, currentContentDb }) => {
  const [onboardingData, setOnboardingData] = useState(() => ({
    ...defaultOnboardingData,
    // Initialize board with user's current contentDb if available
    board: currentContentDb || '',
  }));
  const [isLoading, setIsLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);

  // Initialize board from currentContentDb on first load (only if board is empty)
  const boardInitializedRef = React.useRef(false);
  useEffect(() => {
    if (currentContentDb && !boardInitializedRef.current && !onboardingData.board) {
      console.log('[OnboardingContext] Initializing board from currentContentDb:', currentContentDb);
      setOnboardingData(prev => ({ ...prev, board: currentContentDb }));
      boardInitializedRef.current = true;
    }
  }, [currentContentDb, onboardingData.board]);

  // Check if user needs onboarding based on profile data
  useEffect(() => {
    const checkIfNewUser = async () => {
      if (!authToken) {
        setIsLoading(false);
        return;
      }

      try {
        // First check if we already have user data with onboarding_complete flag
        if (userData?.onboarding_complete === true) {
          console.log('User has onboarding_complete flag, skipping onboarding');
          setOnboardingData(prev => ({ ...prev, completed: true }));
          setIsLoading(false);
          return;
        }

        // Fetch profile to check onboarding_complete flag
        const profileRes = await axios.get(`${FASTAPI_BASE_URL}/profile`, {
          headers: { Authorization: authToken },
        });

        const profile = profileRes.data;

        if (profile?.onboarding_complete === true) {
          console.log('Profile has onboarding_complete=true, skipping onboarding');
          setOnboardingData(prev => ({ ...prev, completed: true }));
        } else {
          console.log('New user detected, showing onboarding');
          setOnboardingData(prev => ({ ...prev, completed: false }));
        }
      } catch (error) {
        console.log('Could not fetch profile:', error.message);
        // If we can't verify, skip onboarding to avoid annoying existing users
        setOnboardingData(prev => ({ ...prev, completed: true }));
      } finally {
        setIsLoading(false);
      }
    };

    checkIfNewUser();
  }, [authToken, userData]);

  // Mark onboarding as complete (save to backend)
  const saveOnboardingComplete = useCallback(async () => {
    if (!authToken || !userEmail) return;

    try {
      // Build the payload with all onboarding data
      // Use currentContentDb as fallback if board wasn't changed during onboarding
      const boardToSave = onboardingData.board || currentContentDb || DEFAULT_BOARD;
      console.log('[OnboardingContext] saveOnboardingComplete - board:', onboardingData.board, 'fallback:', currentContentDb, 'final:', boardToSave);

      const payload = {
        onboarding_complete: true,
        // Profile fields
        grade: onboardingData.profile?.grade || '',
        age: onboardingData.profile?.age ? parseInt(onboardingData.profile.age, 10) : null,
        passion: onboardingData.profile?.passion || '',
        favorite_stem_subject: onboardingData.profile?.favorite_stem_subject || '',
        extracurricular_activity: onboardingData.profile?.extracurricular_activity || '',
        preferred_learning_style: onboardingData.profile?.preferred_learning_style || 'visual',
        // Board/curriculum - stored as content_db
        content_db: boardToSave,
      };

      await axios.post(
        `${FASTAPI_BASE_URL}/student/${encodeURIComponent(userEmail)}/update-profile`,
        payload,
        { headers: { Authorization: authToken } }
      );
      console.log('Saved onboarding data to profile:', payload);

      // Refetch profile to update AuthContext with new contentDb
      if (refetchProfile) {
        try {
          await refetchProfile();
          console.log('Refetched profile after onboarding');
        } catch (err) {
          console.log('Could not refetch profile:', err.message);
        }
      }
    } catch (error) {
      console.log('Could not save onboarding status:', error.message);
    }
  }, [authToken, userEmail, onboardingData, refetchProfile, currentContentDb]);

  // Update subjects
  const updateSubjects = useCallback((subjects) => {
    setOnboardingData(prev => ({ ...prev, subjects }));
  }, []);

  // Update learning goal
  const updateLearningGoal = useCallback((learningGoal) => {
    setOnboardingData(prev => ({ ...prev, learningGoal }));
  }, []);

  // Update profile data
  const updateProfile = useCallback((profile) => {
    setOnboardingData(prev => ({ ...prev, profile }));
  }, []);

  // Update board selection
  const updateBoard = useCallback((board) => {
    console.log('[OnboardingContext] updateBoard called with:', board);
    setOnboardingData(prev => ({ ...prev, board }));
  }, []);

  // Complete onboarding
  const completeOnboarding = useCallback(async () => {
    setOnboardingData(prev => ({ ...prev, completed: true }));
    await saveOnboardingComplete();
  }, [saveOnboardingComplete]);

  // Skip onboarding
  const skipOnboarding = useCallback(async () => {
    setOnboardingData(prev => ({ ...prev, completed: true, skipped: true }));
    await saveOnboardingComplete();
  }, [saveOnboardingComplete]);

  // Reset onboarding (for testing)
  const resetOnboarding = useCallback(() => {
    setOnboardingData(defaultOnboardingData);
    setCurrentStep(0);
  }, []);

  // Navigate steps
  const nextStep = useCallback(() => {
    setCurrentStep((prev) => prev + 1);
  }, []);

  const prevStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(0, prev - 1));
  }, []);

  const goToStep = useCallback((step) => {
    setCurrentStep(step);
  }, []);

  const value = {
    // State
    onboardingData,
    isLoading,
    currentStep,
    isCompleted: onboardingData.completed,
    subjects: onboardingData.subjects,
    learningGoal: onboardingData.learningGoal,
    profile: onboardingData.profile,
    board: onboardingData.board,

    // Actions
    updateSubjects,
    updateLearningGoal,
    updateProfile,
    updateBoard,
    completeOnboarding,
    resetOnboarding,
    skipOnboarding,
    nextStep,
    prevStep,
    goToStep,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export default OnboardingContext;
